<template>
  <div ref="rootRef" class="relative inline-block">
    <div ref="triggerRef" @click="toggle"><slot /></div>
    <teleport to="body">
      <transition name="ui-menu">
        <div
          v-if="open"
          ref="menuRef"
          class="z-[1000] w-fit min-w-[10rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          :style="menuStyle"
        >
          <slot name="menu" :close="close" />
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useAnchorPopover } from './useAnchorPopover'

/** UiMenu — Vuetify <v-menu> replacement (click-toggle popover).
 * Uses position: fixed + Teleport to body so the menu is not clipped by
 * ancestor overflow (e.g. inside a dialog body). Its z-index (1000) sits
 * above every stacked UiDialog (100 + 10 per open dialog) and below the
 * snackbar (2000). */
const props = withDefaults(
  defineProps<{
    closeOnContentClick?: boolean
    fitContent?: boolean
    /** Don't open on trigger click (disabled date/time/color fields). */
    disabled?: boolean
  }>(),
  {
    closeOnContentClick: true,
    fitContent: false,
    disabled: false,
  },
)

const open = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)

const { style: anchorStyle } = useAnchorPopover(
  triggerRef,
  open,
  240,
  props.fitContent,
  menuRef,
)

const menuStyle = anchorStyle

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) close()
  },
)

function toggle() {
  if (props.disabled && !open.value) return
  open.value = !open.value
}
function close() {
  open.value = false
}

function onDocClick(e: MouseEvent) {
  if (!open.value) return
  const target = e.target as Node
  // The menu is teleported to body, so rootRef won't contain it.
  if (
    rootRef.value &&
    !rootRef.value.contains(target) &&
    menuRef.value &&
    !menuRef.value.contains(target)
  ) {
    close()
  } else if (
    props.closeOnContentClick &&
    (menuRef.value?.contains(target) ?? false)
  ) {
    close()
  }
}

function onKeydown(e: KeyboardEvent) {
  // Escape closes only the open menu: mark it handled so an enclosing
  // UiModal (window listener, runs after this document one) ignores it.
  if (e.key === 'Escape' && open.value && !e.defaultPrevented) {
    e.preventDefault()
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.ui-menu-enter-active,
.ui-menu-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.ui-menu-enter-from,
.ui-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
